import { z } from "zod";

export const MODIFY_PROMPT_TOOL_NAME = "modify_prompt" as const;

export const MODIFY_PROMPT_TOOL_DESCRIPTION = `Perform a find-and-replace edit on a specific agent's prompt.

Finds \`old_string\` in the agent's current prompt (rendered as markdown) and replaces it with \`new_string\`. Use \`replace_all\` to replace every occurrence; default replaces only the first.

INPUT SCHEMA
- agent_id (string, required): The ID of the agent whose prompt to edit.
- agent_name (string, required): The agent's human-readable display name as shown in the dashboard (e.g. "Customer Support"), taken from the <metadata> block or the <resource name="…"/> mention. This is shown to the user in the suggestion card — NEVER pass the id/uuid here. If you only know the id, look the agent up to get its name before calling this tool.
- old_string (string, required): The exact text to find. Must appear verbatim in the agent's current prompt markdown. If not found, the tool returns an error — check the prompt content and retry with corrected text.
- new_string (string, required): The replacement text.
- replace_all (boolean, optional): When true, replaces every occurrence of old_string. Default: false (replace first occurrence only).
- description (string, required): A short, human-readable label for what this edit does, shown to the user as the title of the suggestion card (e.g. "Tighten the refund-policy wording"). ALWAYS provide it; keep it to a few words. Distinct from explanation — description is the user-facing label, explanation is your private reasoning.
- explanation (string, optional): Brief note describing the intent of the edit (for auditability).

RESULT
On success: a confirmation that the prompt was updated.
On failure: an error message explaining why — agent not found, old_string not found, or the replacement would produce an invalid prompt. Read the message and retry with corrected arguments.`;

export const ModifyPromptArgsSchema = z.object({
  agent_id: z.string().describe("The ID of the agent whose prompt to edit."),
  agent_name: z
    .string()
    .describe(
      'The agent\'s human-readable display name (e.g. "Customer Support"), shown to the user in the suggestion card. Never the id/uuid — look the agent up by id if the name is not already known.'
    ),
  old_string: z
    .string()
    .describe("The exact text to find in the agent's current prompt. Must appear verbatim."),
  new_string: z.string().describe("The replacement text."),
  replace_all: z
    .boolean()
    .optional()
    .describe(
      "When true, replaces every occurrence of old_string. Default: false (first occurrence only)."
    ),
  description: z
    .string()
    .describe(
      'A short, human-readable label for what this edit does, shown to the user as the title of the suggestion card (e.g. "Tighten the refund-policy wording"). Always provide it. Distinct from explanation, which is your private reasoning.'
    ),
  explanation: z
    .string()
    .optional()
    .describe("Optional short note describing the intent of the edit.")
});

export type ModifyPromptArgs = z.infer<typeof ModifyPromptArgsSchema>;
